import { useNavigate, useRouterState } from '@tanstack/react-router'
import { AddServer } from '@/features/servers/Servers'
import { useVisibleAlerts } from '@/features/alerts/Alerts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Project, ProjectList, SessionsList } from '@one-deployment/api'
import { qk } from '@/lib/query'
import { isSessionPath, listedSessions, sessionLabel, useCreateSession } from '@/features/sessions/data'
import { PanelLeft, Plus, UserRound } from 'lucide-react'
import { Menu } from '@/components/ui'
import { Button } from '@/components/shadcn/button'
import { useLayout } from '@/stores/layout'
import { useCan } from '@/components/RoleGate'
import { enc, get, post } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useMe } from '@/features/auth/useMe'
import { useOverview } from './useOverview'
import { ProjectRelease, SessionGit } from '@/features/sessions/GitBar'
import { ProjectTabs } from '@/features/projects/Overview'

type Crumb = { label: string; to?: string }
type Names = { project?: (key: string) => string | undefined; session?: (key: string, id: string) => string | undefined }

const SECTIONS: Record<string, string> = { s: 'Sessions', issues: 'Issues', intake: 'Tickets from text', scope: 'Detailed scope', errors: 'Error review', releases: 'Releases', archive: 'Archive', ci: 'CI', settings: 'Settings' }
const title = (p: string) => p.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase())

/** Where you are, by name: project name, then section, then the thing in it. */
export function crumbs(path: string, names: Names = {}): Crumb[] {
  const parts = path.split('/').filter(Boolean)
  if (parts.length === 0) return [{ label: 'Home' }]
  if (parts[0] === 'p' && parts[1]) {
    const key = parts[1]
    const base = `/p/${key}`
    const out: Crumb[] = [{ label: names.project?.(key) ?? key, to: base }]
    const [, , section, item, sub] = parts
    if (section === 'w' && item) out.push({ label: item, to: `${base}/w/${item}` })
    // An open session follows the project directly: a "Sessions" crumb only led back to the project page.
    else if (section === 's' && item) out.push({ label: names.session?.(key, item) ?? 'Session' })
    else if (section) {
      out.push({ label: SECTIONS[section] ?? title(section), to: `${base}/${section}` })
      if (item) out.push({ label: title(item) })
    }
    if (sub) out.push({ label: title(sub) })
    return out
  }
  // Settings' tabs say which area it is.
  if (parts[0] === 'settings') return [{ label: 'Settings' }]
  if (parts[0] === 'account') return [{ label: 'My account' }]
  return parts.slice(0, 2).map((p, i) => ({ label: i === 0 ? title(p) : p }))
}

export function TopBar() {
  const path = useRouterState({ select: (s) => s.location.pathname })
  const searchTab = useRouterState({ select: (s) => (s.location.search as { tab?: string }).tab })
  const qc = useQueryClient()
  // Names come from what the page has already loaded; these observers only
  // read the cache (enabled: false), so the bar never fetches on its own.
  // Listening to every cache event instead re-rendered on each one and
  // starved the session page's lazy load: it never left its skeleton.
  const [, , key, section, item] = path.split('/')
  // Each still carries its fetcher: if the bar's observer is the first one on a
  // key, an invalidate refetches with its options, and with no queryFn that
  // put "Missing queryFn" on the project page itself.
  const project = useQuery<Project>({ queryKey: qk.project(key ?? ''), queryFn: ({ signal }) => get<Project>(`/api/projects/${enc(key ?? '')}`, signal), enabled: false })
  const list = useQuery<ProjectList>({ queryKey: qk.projects(), queryFn: ({ signal }) => get<ProjectList>('/api/projects', signal), enabled: false })
  const sessions = useQuery<SessionsList>({ queryKey: qk.projectSessions(key ?? ''), queryFn: ({ signal }) => get<SessionsList>(`/api/projects/${enc(key ?? '')}/sessions`, signal), enabled: false })
  const names: Names = {
    project: (k) => project.data?.name ?? list.data?.projects.find((p) => p.key === k)?.name,
    session: () => { const x = section === 's' ? sessions.data?.sessions.find((s) => s.id === item) : undefined; return x && sessionLabel(x) },
  }
  const me = useMe().data
  const alerts = useVisibleAlerts(useOverview().data?.alerts ?? [])
  const down = alerts.some((a) => a.level === 'down')
  const nav = useNavigate()
  const inSession = isSessionPath(path)
  const { navInSession, setNavInSession } = useLayout()
  // On a project's own page the header holds its tabs beside its name, in place of deeper crumbs.
  const tabs = path.startsWith('/p/') && !!key && !inSession && (!section || section === 'settings' || section === 'ci')
  const trail = tabs ? crumbs(path, names).slice(0, 1) : crumbs(path, names)

  return (
    <header className="flex h-11 shrink-0 items-center justify-between border-b border-border px-4">
      {inSession && <Button variant="ghost" size="icon-sm" aria-label={navInSession ? 'Hide the sidebar' : 'Show the sidebar'} title={navInSession ? 'Hide the sidebar' : 'Show the sidebar'}
        onClick={() => setNavInSession(!navInSession)} className="mr-2 size-6 rounded-xs text-fg-subtle hover:bg-hover"><PanelLeft size={14} /></Button>}
      <ol className="flex min-w-0 items-center gap-1.5 text-sm">
        {trail.map((c, i, all) => (
          <li key={i} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && <span className="text-fg-faint">/</span>}
            {inSession && i === all.length - 1 ? (
              <SessionSwitcher project={key!} current={item!} label={c.label} />
            ) : c.to && i < all.length - 1 ? (
              <a href={c.to} onClick={(e) => { e.preventDefault(); void nav({ to: c.to! }) }} className="truncate text-fg-subtle hover:text-fg">
                {c.label}
              </a>
            ) : (
              <span className="truncate text-fg">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
      {tabs && <div className="ml-4"><ProjectTabs project={key!} ci={!!project.data?.settings.ci?.enabled}
        tab={section === 'settings' ? 'settings' : section === 'ci' ? 'ci' : searchTab === 'stages' ? 'stages' : 'tickets'} /></div>}
      {inSession && <div className="ml-3"><SessionGit project={key!} sessionId={item!} /></div>}
      <div className="flex-1" />
      <div className="flex items-center gap-3">
        {path.startsWith('/p/') && key && <ProjectRelease project={key} />}
        {path === '/servers' && <AddServer />}
        <Menu
          trigger={
            <Button variant="ghost" size="icon-sm" className="relative size-7 rounded-full bg-surface-3 text-fg-subtle hover:bg-hover"
              aria-label={alerts.length ? `Account menu, ${alerts.length} alert${alerts.length === 1 ? '' : 's'}` : 'Account menu'}>
              {/* The Linear picture, as Linear shows it; a plain silhouette otherwise, never initials. */}
              {me?.avatar ? <img src={me.avatar} alt="" className="size-7 rounded-full object-cover" referrerPolicy="no-referrer" /> : <UserRound size={14} />}
              {/* Alerts live behind the picture: a dot says there are some. */}
              {alerts.length > 0 && <span data-testid="alert-dot" className={cn('absolute -right-0.5 -top-0.5 size-2.5 rounded-full ring-2 ring-bg', down ? 'bg-err' : 'bg-warn')} />}
            </Button>
          }
          items={[
            {
              label: <span className="flex w-full items-center gap-2">Alerts{alerts.length > 0 && <span className={cn('ml-auto rounded-full px-1.5 text-2xs tabular-nums', down ? 'bg-err/15 text-err' : 'bg-warn/15 text-warn')}>{alerts.length}</span>}</span>,
              onSelect: () => void nav({ to: '/alerts' }),
            },
            { label: 'My account', onSelect: () => void nav({ to: '/account' }) },
            'sep',
            {
              label: 'Sign out',
              onSelect: () => {
                void post('/auth/logout').finally(() => {
                  qc.clear()
                  void nav({ to: '/login' })
                })
              },
            },
          ]}
        />
      </div>
    </header>
  )
}

/** The open session's name, as a menu of the project's other sessions and a new one. */
function SessionSwitcher({ project, current, label }: { project: string; current: string; label: string }) {
  const nav = useNavigate()
  const sessions = useQuery<SessionsList>({ queryKey: qk.projectSessions(project), enabled: false })
  const create = useCreateSession(project)
  const canCreate = useCan('session.manage', project)
  const items = listedSessions(sessions.data?.sessions ?? [], current).map((s) => ({
    label: <span className={cn('flex items-center gap-2', s.id === current && 'font-medium text-fg')}>{sessionLabel(s)}{s.jobId && <span className="text-2xs text-fg-faint">robot</span>}</span>,
    onSelect: () => void nav({ to: '/p/$project/s/$session', params: { project, session: s.id } }),
  }))
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Menu
        trigger={<Button variant="ghost" className="h-auto min-w-0 shrink justify-start gap-1 truncate rounded-xs px-1 py-0 text-sm font-normal text-fg hover:bg-hover" aria-label="Switch session">{create.isPending ? 'Starting…' : label}<span className="text-fg-faint">▾</span></Button>}
        items={canCreate ? [...items, 'sep', { label: <span className="flex items-center gap-1"><Plus size={12} />New session</span>, onSelect: () => create.mutate() }] : items}
      />
      {create.error && <span className="truncate text-xs text-err">{(create.error as Error).message}</span>}
    </span>
  )
}
